/* Copyright (c) 2017 Backpack Health LLC */

import * as _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import './Icons.styles.scss';

const IconSizeShape = PropTypes.oneOf(['default', 'extraSmall', 'small', 'medium', 'large', 'nav']);

const IconSizeNames = {
  default: 'default',
};

const IconBoundSizes = {
  default: {},
};


function generateFontAwesomeIcon(iconName, fontSize, bounds, lineHeight) {
  const FAIcon = (props) => {
    const classes = classnames(
      'fa',
      `fa-${iconName}`,
      `size-${props.size}`,
      {
        'is-clickable': !!props.onClick,
        'font-color-input-error': props.color === 'error',
        'font-color-input-active': props.color === 'active',
        'font-color-input-inactive': props.color === 'inactive',
        'font-color-input-white': props.color === 'white',
        'font-color-warning': props.color === 'warning',
        'font-color-profile': props.color === 'profile',
        'font-color-profile-highlight': props.color === 'profile-highlight',
        'font-color-middle-gray': props.color === 'middle-gray',
        'font-color-dark-gray': props.color === 'darkGray',
        'font-color-meddark-gray': props.color === 'medDarkGray',
        'font-color-gsMedDark': props.color === 'gsMedDark',
        'font-color-gsDark': props.color === 'gsDark',
        'font-color-gsDarkest': props.color === 'gsDarkest',
      }
    );
    const styles = _.assign({}, {
      lineHeight: props.lineHeight ? props.lineHeight : lineHeight,
      width: bounds,
      height: bounds,
      fontSize,
    });
    return <div className="cmp-icon" onClick={props.onClick}><i style={styles} className={classes} /></div>;
  };
  FAIcon.propTypes = {
    color: PropTypes.oneOf(['white', 'default', 'error', 'light', 'inactive', 'warning', 'profile', 'profile-highlight', 'medDarkGray', 'middle-gray', 'darkGray', 'gsMedDark', 'gsDarkest']),
    lineHeight: PropTypes.string,
    size: IconSizeShape,
    onClick: PropTypes.func,
  };
  FAIcon.defaultProps = {
    size: IconSizeNames.default,
  };
  return FAIcon;
}

function generateMaterialIcon(materialName, fontSize, bounds, lineHeight) {
  const MaterialIcon = (props) => {
    const boundSize = props.useSmallerBounds ? IconBoundSizes[props.size] : {};
    const size = props.useSmallerBounds ? boundSize.fontSize : props.size;

    const classes = classnames(
      'material-icons',
      `size-${size}`,
      {
        'is-clickable': props.isClickable,
        'font-color-input-error': props.color === 'error',
        'font-color-input-active': props.color === 'active',
        'font-color-input-inactive': props.color === 'inactive',
        'font-color-light': props.color === 'light',
        'font-color-warning': props.color === 'warning',
        'font-color-input-white': props.color === 'white',
        'font-color-middle-gray': props.color === 'middle-gray',
        'font-color-dark-gray': props.color === 'darkGray',
        'font-color-meddark-gray': props.color === 'medDarkGray',
        'font-color-profile': props.color === 'profile',
        'font-color-gsMedDark': props.color === 'gsMedDark',
        'font-color-gsDark': props.color === 'gsDark',
        'font-color-gsDarkest': props.color === 'gsDarkest',
      }
    );

    const styles = _.assign({}, boundSize, {
      lineHeight: props.lineHeight ? props.lineHeight : lineHeight,
      fontSize,
      width: bounds,
      height: bounds,
    });

    return <i style={styles} className={classes}>{materialName}</i>;
  };
  MaterialIcon.propTypes = {
    color: PropTypes.oneOf(['white', 'default', 'error', 'light', 'inactive', 'warning', 'active', 'profile', 'profile-accent', 'medDarkGray', 'middle-gray', 'darkGray', 'gsMedDark', 'gsDarkest']),
    size: IconSizeShape,
    isClickable: PropTypes.bool,
    lineHeight: PropTypes.string,
    useSmallerBounds: PropTypes.bool,
  };
  MaterialIcon.defaultProps = {
    size: IconSizeNames.default,
  };
  return MaterialIcon;
}

const IconWithText = (props) => {
  const classes = classnames(
    'cmp-icon-with-text',
    {
      'is-disabled': props.isDisabled,
      'is-clickable': _.isFunction(props.onClick),
      'font-color-input-error': props.color === 'error',
      'font-color-input-active': props.color === 'active',
      'font-color-input-inactive': props.color === 'inactive',
      'font-color-light': props.color === 'light',
    }
  );
  return (
    <div className={classes} onClick={props.onClick}>
      {
        props.leftText ? (
          <span className="left-side">
            {props.leftText}
          </span>
        ) : null
      }
      <span>
        {props.icon}
      </span>
      {
        props.rightText ? (
          <span className="right-side">
            {props.rightText}
          </span>
        ) : null
      }
    </div>
  );
};
IconWithText.propTypes = {
  color: PropTypes.oneOf(['default', 'error', 'light', 'active', 'inactive', 'gsMedDark', 'gsDarkest']),
  leftText: PropTypes.any,
  rightText: PropTypes.any,
  icon: PropTypes.element,
  onClick: PropTypes.func,
  isDisabled: PropTypes.bool,
};

export default {
  // these icons have been set to specific size, changing them will mess things up. bad idea? maybe.
  Language: generateMaterialIcon('language', '20px', '20px', '20px'),

  Write: generateFontAwesomeIcon('pencil', '20px', '20px', '20px'),
  Calendar: generateMaterialIcon('event', '20px', '20px', '30px'),
  Anatomy: generateMaterialIcon('accessibility', '20px', '20px', '30px'),
  StoppingReason: generateFontAwesomeIcon('minus-circle', '20px', '20px', '30px'),

  Add: generateMaterialIcon('add', '30px', '40px', '40px'),
  FlagCircle: generateFontAwesomeIcon('circle-thin', '18px', '24px', '24px'),
  FlaggedCircle: generateFontAwesomeIcon('circle', '18px', '24px', '24px'),

  FileOtherLarge: generateFontAwesomeIcon('file-o', '100px', '100px', '100px'),

  Trashcan: generateMaterialIcon('delete', '40px', '56px', '56px'),
  Timer: generateMaterialIcon('access_time', '20px', '20px', '30px'),
  Frown: generateMaterialIcon('sentiment_dissatisfied', '20px', '20px', '20px'),

  Email: generateFontAwesomeIcon('envelope', '20px', '20px', '24px'),
  Lock: generateFontAwesomeIcon('lock', '20px', '20px', '24px'),
  Shield: generateFontAwesomeIcon('shield', '20px', '20px', '24px'),

  // icons below might be okay to change (move above this line after changing them)
  Search: generateFontAwesomeIcon('search', '28px', '56px', '56px'),
  Messages: generateFontAwesomeIcon('comment', '28px', '56px', '56px'),
  Share: generateFontAwesomeIcon('share', '28px', '56px', '56px'),
  Close: generateFontAwesomeIcon('times-circle', '40px', '56px', '56px'),
  BackArrow: generateFontAwesomeIcon('arrow-circle-o-left', '40px', '56px', '56px'),
  Notes: generateFontAwesomeIcon('pencil', '20px', '30px', '30px'),
  Files: generateFontAwesomeIcon('file-o', '20px', '30px', '30px'),
  Ellipsis: generateFontAwesomeIcon('ellipsis-h', '40px', '56px', '56px'),
  Download: generateFontAwesomeIcon('download', '20px', '56px', '56px'),
  ShareAlt: generateFontAwesomeIcon('share-alt', '20px', '56px', '56px'),
  CreditCard: generateFontAwesomeIcon('credit-card', '16px', '16px', '16px'),
  // Warning: generateFontAwesomeIcon('exclamation-circle', '156px', '156px',156px'),

  ArrowDropDown: generateMaterialIcon('arrow_drop_down', '20px', '20px', '20px'),

  ExpandSection: generateMaterialIcon('keyboard_arrow_down', '40px', '40px', '40px'),

  IconWithText,
};
